/**
 * ============================================================================
 *  File        : frontend/src/pages/CaseSettlementP1Page.tsx
 *  Module      : pages
 *  Purpose     : /cases/:id/settlements/p1 一回目精算（spec §8.3）— P1 試算 + N34 手入力
 *  Author      : EF Engineering
 *  Created     : 2026-05-31
 *  Updated     : 2026-05-31
 *  License     : Proprietary - ECO FAITH Platform v3.0
 * ============================================================================
 *
 *  spec §8.3：使用者面 label 不得露出 L34/M34/N34 cell 代碼。
 *  - 一回目試算總額（税込）= l34_total_incl_tax
 *  - 50% 參考前受金 = m34_reference_50pct
 *  - 試算前税別小計 = l_sum_excl_tax
 *  - EF 實際請求金額（手入力）= N34，呼叫 manual-amount endpoint
 *  每次「試算」呼叫產生新 run（non-overwrite），畫面保留 run 歷史。
 *  金額一律字串型 Decimal，禁 float（CLAUDE.md §2）。
 */

import { useMemo, useState, type ReactElement } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import { useParams } from 'react-router-dom'
import { getCase, type CaseItemDto } from '../api/cases'
import {
  calculateP1,
  submitP1ManualAmount,
  type CalculateItemDto,
  type P1ResultDto,
} from '../api/settlements'
import { ApiError } from '../api/client'
import { usePermissions } from '../hooks/usePermissions'
import { PERMISSIONS } from '../lib/permissions'
import { useStepUp } from '../components/StepUpDialog'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Badge } from '../components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card'
import { Skeleton } from '../components/ui/skeleton'
import { toast } from '../components/ui/sonner'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table'

interface PageProps {
  t: (key: string) => string
}

/** 試算列：由案件品項預填，使用者可微調重量・最低単価・袋重。 */
interface SettlementRow {
  itemManagementNo: string
  itemName: string
  grossWeightWithBagKg: string
  minUnitPrice: string
  bagUnitWeightKg: string
}

/** 帶手入力金額的 P1 run 結果。 */
interface RunEntry {
  result: P1ResultDto
  /** N34 EF 實際請求金額（手入力，未填為 null）。 */
  manualAmount: string | null
}

/** 案件品項 → 試算列。gross_weight = 概算重量(フ入り)；無值以 0 起算供使用者填。 */
function toSettlementRow(item: CaseItemDto): SettlementRow {
  return {
    itemManagementNo: String(item.line_no),
    itemName: item.item_name,
    grossWeightWithBagKg: item.estimated_weight_kg ?? '',
    minUnitPrice: item.min_unit_price ?? '',
    bagUnitWeightKg: '',
  }
}

/** 試算列 → 後端 CalculateItemDto。package_type 預設 flexible（L 計算僅用 gross×price）。 */
function toCalculateItem(row: SettlementRow): CalculateItemDto {
  return {
    item_management_no: row.itemManagementNo,
    gross_weight_with_bag_kg: row.grossWeightWithBagKg.trim(),
    min_unit_price: row.minUnitPrice.trim(),
    bag_count: 0,
    package_type: 'flexible',
    bag_unit_weight_kg: row.bagUnitWeightKg.trim() || '0',
  }
}

/** 千分位格式化（顯示用；不改動原始 Decimal 字串值）。 */
function formatAmount(value: string): string {
  const num = Number(value)
  if (!Number.isFinite(num)) return value
  return num.toLocaleString('ja-JP', { maximumFractionDigits: 2 })
}

export function CaseSettlementP1Page({ t }: PageProps): ReactElement {
  const params = useParams()
  const caseId = params.id ?? ''
  const { can } = usePermissions()
  const canCalculate = can(PERMISSIONS.settlementP1)
  // N34 手入力 = 高風險端點 → 送出前先 2FA step-up（spec §9.2）。
  const stepUp = useStepUp(t)

  const caseQuery = useQuery({
    queryKey: ['case', caseId],
    queryFn: () => getCase(caseId),
    enabled: caseId !== '',
  })

  const [rows, setRows] = useState<SettlementRow[] | null>(null)
  const [runs, setRuns] = useState<RunEntry[]>([])
  const [manualInput, setManualInput] = useState('')

  /** 由案件品項初始化試算列（一次性，之後沿用使用者編輯）。 */
  const effectiveRows = useMemo<SettlementRow[]>(() => {
    if (rows !== null) return rows
    return (caseQuery.data?.items ?? []).map(toSettlementRow)
  }, [rows, caseQuery.data])

  const caseNo = caseQuery.data?.case_no ?? ''

  function updateRow(index: number, patch: Partial<SettlementRow>): void {
    setRows((prev) => {
      const base = prev ?? effectiveRows
      return base.map((r, i) => (i === index ? { ...r, ...patch } : r))
    })
  }

  const calcMutation = useMutation({
    mutationFn: () =>
      calculateP1(caseId, {
        case_no: caseNo,
        items: effectiveRows.map(toCalculateItem),
      }),
    onSuccess: (result) => {
      // 每次試算產生新 run，append 不覆蓋（non-overwrite 可視化）。
      setRuns((prev) => [{ result, manualAmount: null }, ...prev])
      toast.success(t('p1.calc.success'))
    },
    onError: (error) => {
      const detail = error instanceof ApiError ? `（${error.message}）` : ''
      toast.error(`${t('p1.calc.error')}${detail}`)
    },
  })

  const manualMutation = useMutation({
    mutationFn: (vars: { runId: number; amount: string; code: string }) =>
      submitP1ManualAmount(vars.runId, { amount: vars.amount }, vars.code),
    onSuccess: (_result, vars) => {
      setRuns((prev) =>
        prev.map((entry) =>
          entry.result.run_id === vars.runId ? { ...entry, manualAmount: vars.amount } : entry,
        ),
      )
      setManualInput('')
      toast.success(t('p1.manual.success'))
    },
    onError: (error) => {
      const detail = error instanceof ApiError ? `（${error.message}）` : ''
      toast.error(`${t('p1.manual.error')}${detail}`)
    },
  })

  const latestRun = runs[0]

  if (caseQuery.isPending) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    )
  }

  if (caseQuery.isError || !caseQuery.data) {
    return (
      <p className="text-sm text-destructive">
        {t('case.detail.loadError')}
        {caseQuery.error instanceof ApiError ? `（${caseQuery.error.message}）` : ''}
      </p>
    )
  }

  return (
    <div className="space-y-4">
      <header className="page-heading">
        <span className="eyebrow">{t('case.stage.p1')}</span>
        <h2 className="text-lg font-semibold">{t('p1.title')}</h2>
        <p className="text-sm text-muted-foreground">{t('p1.subtitle')}</p>
      </header>

      {/* 試算品項格 — 由案件品項預填，可微調 */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>{t('p1.items.title')}</CardTitle>
          {canCalculate ? (
            <Button onClick={() => calcMutation.mutate()} disabled={calcMutation.isPending}>
              {calcMutation.isPending ? t('p1.calc.running') : t('p1.calc.button')}
            </Button>
          ) : (
            <Badge variant="outline">{t('rbac.forbidden')}</Badge>
          )}
        </CardHeader>
        <CardContent>
          {effectiveRows.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('p1.items.empty')}</p>
          ) : (
            <div className="overflow-x-auto rounded-md border border-border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('case.detail.lineNo')}</TableHead>
                    <TableHead>{t('field.itemName')}</TableHead>
                    <TableHead className="min-w-[8rem]">{t('p1.field.grossWeight')}</TableHead>
                    <TableHead className="min-w-[8rem]">{t('field.minUnitPrice')}</TableHead>
                    <TableHead className="min-w-[7rem]">{t('field.bagUnitWeight')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {effectiveRows.map((row, index) => (
                    <TableRow key={row.itemManagementNo}>
                      <TableCell>{row.itemManagementNo}</TableCell>
                      <TableCell className="font-medium">{row.itemName}</TableCell>
                      <TableCell>
                        <Input
                          inputMode="decimal"
                          value={row.grossWeightWithBagKg}
                          disabled={!canCalculate}
                          aria-label={t('p1.field.grossWeight')}
                          onChange={(e) => updateRow(index, { grossWeightWithBagKg: e.target.value })}
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          inputMode="decimal"
                          value={row.minUnitPrice}
                          disabled={!canCalculate}
                          aria-label={t('field.minUnitPrice')}
                          onChange={(e) => updateRow(index, { minUnitPrice: e.target.value })}
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          inputMode="decimal"
                          value={row.bagUnitWeightKg}
                          disabled={!canCalculate}
                          aria-label={t('field.bagUnitWeight')}
                          onChange={(e) => updateRow(index, { bagUnitWeightKg: e.target.value })}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* 最新試算結果 — 使用者面 label，不露 cell 代碼 */}
      {latestRun ? (
        <Card>
          <CardHeader>
            <CardTitle>
              {t('p1.result.title')}（run #{latestRun.result.run_id}）
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <dl className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <ResultStat
                label={t('p1.result.preTaxSum')}
                value={`¥${formatAmount(latestRun.result.l_sum_excl_tax)}`}
              />
              <ResultStat
                label={t('p1.result.inclTaxTotal')}
                value={`¥${formatAmount(latestRun.result.l34_total_incl_tax)}`}
                emphasis
              />
              <ResultStat
                label={t('p1.result.reference50')}
                value={`¥${formatAmount(latestRun.result.m34_reference_50pct)}`}
              />
            </dl>

            {/* N34 EF 實際請求金額（手入力）*/}
            <div className="rounded-md border border-border p-4">
              <p className="text-sm font-medium">{t('p1.manual.label')}</p>
              <p className="mb-2 text-xs text-muted-foreground">{t('p1.manual.hint')}</p>
              {latestRun.manualAmount !== null ? (
                <p className="text-lg font-semibold">
                  ¥{formatAmount(latestRun.manualAmount)}{' '}
                  <Badge variant="secondary">{t('p1.manual.saved')}</Badge>
                </p>
              ) : canCalculate ? (
                <form
                  className="flex max-w-md gap-2"
                  onSubmit={(e) => {
                    e.preventDefault()
                    const amount = manualInput.trim()
                    if (amount) {
                      stepUp.request((code) =>
                        manualMutation.mutate({ runId: latestRun.result.run_id, amount, code }),
                      )
                    }
                  }}
                >
                  <Input
                    inputMode="decimal"
                    value={manualInput}
                    placeholder={t('p1.manual.placeholder')}
                    aria-label={t('p1.manual.label')}
                    data-testid="p1-manual-amount-input"
                    onChange={(e) => setManualInput(e.target.value)}
                  />
                  <Button
                    type="submit"
                    data-testid="p1-manual-amount-submit"
                    disabled={manualMutation.isPending || !manualInput.trim()}
                  >
                    {manualMutation.isPending ? t('p1.manual.saving') : t('p1.manual.submit')}
                  </Button>
                  {stepUp.dialog}
                </form>
              ) : (
                <Badge variant="outline">{t('rbac.forbidden')}</Badge>
              )}
            </div>
          </CardContent>
        </Card>
      ) : null}

      {/* run 歷史 — 證明 re-calc 產生新 run，不覆蓋 */}
      {runs.length > 0 ? (
        <Card>
          <CardHeader>
            <CardTitle>{t('p1.history.title')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="rounded-md border border-border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('p1.history.run')}</TableHead>
                    <TableHead className="text-right">{t('p1.result.preTaxSum')}</TableHead>
                    <TableHead className="text-right">{t('p1.result.inclTaxTotal')}</TableHead>
                    <TableHead className="text-right">{t('p1.result.reference50')}</TableHead>
                    <TableHead className="text-right">{t('p1.manual.label')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {runs.map((entry) => (
                    <TableRow key={entry.result.run_id}>
                      <TableCell>#{entry.result.run_id}</TableCell>
                      <TableCell className="text-right">
                        ¥{formatAmount(entry.result.l_sum_excl_tax)}
                      </TableCell>
                      <TableCell className="text-right">
                        ¥{formatAmount(entry.result.l34_total_incl_tax)}
                      </TableCell>
                      <TableCell className="text-right">
                        ¥{formatAmount(entry.result.m34_reference_50pct)}
                      </TableCell>
                      <TableCell className="text-right">
                        {entry.manualAmount !== null ? `¥${formatAmount(entry.manualAmount)}` : '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      ) : null}
    </div>
  )
}

function ResultStat({
  label,
  value,
  emphasis,
}: {
  label: string
  value: string
  emphasis?: boolean
}): ReactElement {
  return (
    <div className="rounded-md border border-border p-4">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className={emphasis ? 'text-2xl font-bold' : 'text-xl font-semibold'}>{value}</dd>
    </div>
  )
}
